/**
 * Dashboard kringen editor — a controlled, NO-API kringen grid.
 *
 * Replicated faithfully from `KringenEditorControlled` in CollectorenTab.tsx
 * (Igor accepted the duplication to keep the 3077-line CollectorenTab untouched).
 * The parent buffers state and only commits to the kringen API on save.
 */
import { useKringSystems } from '@/stores/systems';

/** Local kring shape — a negative `id` marks a not-yet-persisted (new) kring. */
export interface LocalKring {
  id: number;
  x_kringnummer: number;
  x_system: string;
  x_legpatroon: number;
  x_length: number;
}

export function DashboardKringenEditor({
  kringen,
  onUpdate,
  onAdd,
  onDelete,
}: {
  kringen: LocalKring[];
  onUpdate: (id: number, field: string, value: string) => void;
  onAdd: () => void;
  onDelete: (id: number) => void;
}) {
  // Selectable kring systems come from the Odoo-defined roster (stores/systems).
  const kringSystems = useKringSystems();
  const knownCodes = new Set(kringSystems.map((s) => s.code));
  return (
    <div>
      <h4 className="text-xs font-bold text-td-navy mb-2">Kringen</h4>
      {kringen.length > 0 && (
        <table className="w-full text-xs mb-2">
          <thead>
            <tr className="text-left text-[10px] font-bold text-td-muted uppercase tracking-wide">
              <th className="px-2 py-1 w-16">#</th>
              <th className="px-2 py-1">Systeem</th>
              <th className="px-2 py-1 w-24">Legpatroon</th>
              <th className="px-2 py-1 w-24">Lengte (m)</th>
              <th className="px-2 py-1 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {kringen.map((k) => (
              <tr key={k.id} className="border-t border-gray-100">
                <td className="px-2 py-1">
                  <input
                    type="number"
                    value={k.x_kringnummer}
                    onChange={(e) => onUpdate(k.id, 'x_kringnummer', e.target.value)}
                    className="w-full px-1 py-0.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-td-blue"
                    aria-label={`Kringnummer voor kring ${k.id}`}
                  />
                </td>
                <td className="px-2 py-1">
                  <select
                    value={k.x_system}
                    onChange={(e) => onUpdate(k.id, 'x_system', e.target.value)}
                    className="w-full px-1 py-0.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-td-blue bg-white"
                    aria-label={`Systeem voor kring ${k.id}`}
                  >
                    {/* An unknown value (legacy data, or a roster that has not loaded
                        yet) stays selectable so the row never silently changes system. */}
                    {!knownCodes.has(k.x_system) && (
                      <option value={k.x_system}>{k.x_system || '—'}</option>
                    )}
                    {kringSystems.map((s) => (
                      <option key={s.code} value={s.code}>{s.label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1">
                  <input
                    type="number"
                    step="any"
                    value={k.x_legpatroon}
                    onChange={(e) => onUpdate(k.id, 'x_legpatroon', e.target.value)}
                    className="w-full px-1 py-0.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-td-blue"
                    aria-label={`Legpatroon voor kring ${k.id}`}
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={k.x_length}
                    onChange={(e) => onUpdate(k.id, 'x_length', e.target.value)}
                    onPaste={(e) => {
                      e.preventDefault();
                      const text = e.clipboardData.getData('text/plain');
                      const values = text
                        .split(/[\n\t\r]+/)
                        .map((v) => v.trim().replace(/[^0-9.,]+/g, '').replace(',', '.'))
                        .filter(Boolean);
                      if (values.length > 0) {
                        const startIdx = kringen.findIndex((kr) => kr.id === k.id);
                        values.forEach((val, i) => {
                          const targetIdx = startIdx + i;
                          if (targetIdx < kringen.length) {
                            onUpdate(kringen[targetIdx].id, 'x_length', val);
                          }
                        });
                      }
                    }}
                    className="w-full px-1 py-0.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-td-blue"
                    aria-label={`Lengte voor kring ${k.id}`}
                  />
                </td>
                <td className="px-2 py-1 text-center">
                  <button
                    type="button"
                    onClick={() => onDelete(k.id)}
                    className="text-red-400 hover:text-red-600 transition"
                    aria-label={`Verwijder kring ${k.x_kringnummer}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {kringen.length === 0 && <p className="text-xs text-td-muted mb-2">Geen kringen</p>}
      <button
        type="button"
        onClick={onAdd}
        className="flex items-center gap-1 text-xs text-td-blue hover:text-td-blue/80 font-semibold transition"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Kring toevoegen
      </button>
    </div>
  );
}
